import { backgroundColorState, blackLighterState, greyDarkerState, themeState, whiteDarkerState } from "@/lib/store";
import { FindInstruments } from "@/models/find-instruments";
import { useEffect, useState } from "react";
import { useAtomValue } from "jotai";

import styles from "./Autocomplete.module.scss";
import Image from "next/legacy/image";
import { ImageNotSupported } from "@mui/icons-material";
import { getMissingLogo } from "@/controllers/stock";
import { Typography } from "@mui/material";
import { useCompanyLogo, useLogosConfig } from "@/lib/logos";

let searchTimeout: NodeJS.Timeout;

interface Props {
    instruments: FindInstruments[];
    onClick?: (instrument: FindInstruments) => void;
}

/**
 * One search result's logo. A component of its own because `useCompanyLogo` is a hook and the
 * results are rendered in a `.map()`: it asks the service for artwork that reads on the page
 * background, and draws a white plate when the service answers that none exists.
 */
const InstrumentLogo = ({ instrument, theme, plated = true, onMissing }: { instrument: any; theme: string; plated?: boolean; onMissing: () => void }) => {
    const logosCfg = useLogosConfig();
    const logo = useCompanyLogo(instrument, "rect", { onMissing });
    const legacyPlate = logosCfg.source === "edge" ? "transparent" : theme === "dark" && plated ? "#c0c0c0" : "transparent";
    if (logo.failed) return <ImageNotSupported />;
    return (
        <div className={styles.logo} style={{ backgroundColor: logo.plate ?? legacyPlate, borderRadius: "4px" }}>
            <Image unoptimized src={logo.src} alt={`${instrument.name} Logo`} layout="fill" objectFit="contain" priority onError={logo.onError} onLoad={logo.onLoad} />
        </div>
    );
};

const Autocomplete = ({ instruments, onClick }: Props) => {
    const logosCfg = useLogosConfig(); // re-render when web flips the logo source or rotates the token
    const theme = useAtomValue(themeState);
    const greyDarker = useAtomValue(greyDarkerState);
    const blackLighter = useAtomValue(blackLighterState);
    const whiteDarker = useAtomValue(whiteDarkerState);
    const backgroundColor = useAtomValue(backgroundColorState);
    const [instrumentsLocal, setInstrumentsLocal] = useState<FindInstruments[]>([]);

    useEffect(() => {
        setInstrumentsLocal(instruments);
    }, [instruments]);

    const reportMissingLogo = (security_id: number) => {
        if (security_id) getMissingLogo(security_id);
    };

    return (
        <div className={styles.HeaderAutocomplete} style={{ display: instrumentsLocal?.length > 0 ? "block" : "none" }}>
            <style jsx>{`
                .${styles.instrument}:hover {
                    background-color: ${theme === "dark" ? blackLighter : whiteDarker} !important;
                }

                .${styles.instrumentsContainer}::-webkit-scrollbar {
                    background-color: ${backgroundColor};
                }
            `}</style>
            <div className={styles.instrumentsContainer}>
                {instrumentsLocal.map((instrument, index) => (
                    <div key={instrument.value} className={styles.instrument} style={{ borderBottom: `1px solid ${theme === "dark" ? greyDarker : whiteDarker}`, backgroundColor }} onClick={() => onClick(instrument)}>
                        <div className={styles.instrumentWrapper}>
                            <InstrumentLogo instrument={instrument} theme={theme} onMissing={() => reportMissingLogo(instrument.security_id)} />
                            <div className={styles.instrumentContent}>
                                <div className={styles.instrumentInfo}>
                                    <Typography variant="body2" className={styles.ticker}>
                                        {instrument.exchange}:{instrument.ticker}
                                    </Typography>
                                    <Typography variant="body2" className={styles.name}>
                                        {instrument.name}
                                    </Typography>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Autocomplete;
